import { ApiError } from '../../api/errors';
import { toIsoDate } from '../../lib/format';
import type { PatternDay } from '../staff/WeeklyPatternEditor';
import {
  DAY_ENTRIES_PATH,
  minutesToText,
  parseHoursInput,
  shiftSpanMinutes,
  type TimesheetDayEntry,
} from './DayEntryEditor';

/**
 * A WHOLE PAY PERIOD OF HOURS, HELD ON THE SCREEN UNTIL ONE BUTTON SENDS IT.
 *
 * The arithmetic and the meaning of a cell live here rather than in the grid, for the reason
 * `staff/schedule.ts` and `register/CountSheet.tsx` each give: the screen is where a rule gets
 * quietly restated, and a second reading of "18:00 to 02:00" or of "this box is empty" is one
 * wrong number waiting to be paid. Nothing here fetches, renders or saves.
 *
 * ## A BLANK IS NOT A ZERO — the rule this sheet is built around
 *
 * It is the count sheet's rule, and it costs more here. "He did not work that Tuesday" and "I have
 * not got to that Tuesday yet" are different facts, and only one of them is a record. So an empty
 * cell is never sent, a cell is only sent once somebody has typed in it, and filling a column from
 * the working week is a gesture the owner makes rather than something this file does to him on
 * load: a suggestion that submits itself would write a month of invented days for anybody who was
 * away, and every one of them would look exactly like a day he had confirmed.
 *
 * ## The times are the record, and the one exception is marked
 *
 * A worked day is its clock-in and clock-out because Honduran overtime prices hours by WHEN they
 * ran. `totalsOnly` — hours with no times — is allowed only for days before the date the owner has
 * set as the one his staff came onto the clock, and a day recorded that way says so for good. Both
 * halves of that are the server's rules; this file mirrors them so the sheet can refuse a cell
 * while the owner is still looking at it, and never so that the sheet becomes a second opinion.
 *
 * ## TYPED WORK IS SACRED
 *
 * The sheet is a fortnight of somebody's wages, read off a diary into a browser tab and held
 * nowhere else until it is sent. So a cell leaves it in exactly two ways — the owner discarding it
 * on purpose, or a save that succeeded, and then only the keys that actually went on the wire. That
 * rule is `sheetAfter` below and it is structural rather than remembered, because the four separate
 * ways this screen used to lose typed days were four call sites each doing something reasonable.
 */

export const DAY_ENTRIES_BULK_PATH = `${DAY_ENTRIES_PATH}/bulk`;

/**
 * How many days one save may carry, mirroring the server's own cap.
 *
 * The shape behind the number is a month for everybody several times over: thirty-one days and a
 * handful of staff. It is mirrored rather than discovered, because the alternative is the owner
 * typing a month and meeting a refusal about the length of an array — the one refusal on this
 * screen that says nothing about his bar.
 */
export const MOST_DAYS_IN_ONE_SAVE = 200;

/** One person-day, as the owner is typing it. Keyed `${staffId}|${date}` on the sheet. */
export interface Cell {
  /** Local "HH:MM" as typed — an end earlier than the start ran past midnight. */
  start: string;
  end: string;
  /**
   * Unpaid break, carried rather than typed: a third box in every cell of a thirty-one-day grid
   * would cost more than it is worth for a figure that is almost always zero here. It comes from
   * the day already on file, or from the working week the cell was filled out of, and it is taken
   * out of the span exactly as it is everywhere else — dropping it would overpay every day of a
   * period that has one.
   */
  breakMinutes: number;
  /** The day's hours when its times are genuinely unknown: "8", "7.5", "7:30". */
  hours: string;
}

/**
 * How one cell is being written: as the times it ran, or as hours alone.
 *
 * Never stored on the cell — it is READ OFF the cell's own contents by `modeOfCell`, and the date
 * and the owner's switch decide only what an empty one offers. Storing it would let a cell go on
 * claiming to be an estimate after the day it sits on had come onto the clock; deciding it from
 * the switch alone loses whatever the other kind of box was holding, which is the defect
 * `modeOfCell` exists to make impossible.
 */
export type CellMode = 'times' | 'hours';

export const BLANK_CELL: Cell = { start: '', end: '', breakMinutes: 0, hours: '' };

export type Sheet = Record<string, Cell>;

export const cellKey = (staffId: string, date: string) => `${staffId}|${date}`;

/** Nothing in any box — as opposed to `cellIsBlank`, which asks about one MODE's boxes. */
export function cellHasNothing(cell: Cell): boolean {
  return cell.start.trim() === '' && cell.end.trim() === '' && cell.hours.trim() === '';
}

/**
 * Everything that may happen to the sheet. Two of the four remove cells, and both have to NAME the
 * keys they remove — see `sheetAfter`.
 */
export type SheetChange =
  /** One cell, as the owner leaves it. An emptied cell is the owner emptying it, so the key goes. */
  | { how: 'typed'; key: string; cell: Cell }
  /** A suggestion written into days nobody has answered for; it never lands on an answered one. */
  | { how: 'filled'; cells: Sheet }
  /** The owner throwing typed work away on purpose — the only discard there is. */
  | { how: 'discarded'; keys: string[] }
  /** A save that came back 200, carrying the exact keys that went on the wire and no others. */
  | { how: 'saved'; keys: string[] };

/**
 * THE ONLY WAY A CELL LEAVES THE SHEET.
 *
 * The rule, stated once so that no screen has to remember it: **nothing prunes typed work except
 * an explicit discard by the owner, or a save that succeeded — and a save prunes only the exact
 * keys it sent.** Everything else may add.
 *
 * It is a reducer rather than four careful `setSheet` calls because that is the difference between
 * a rule and a habit. What the habit cost, measured on this screen: a successful save reset the
 * whole sheet while sending only the cells that read as days, so a fortnight typed with one
 * clock-out reading "18:0" lost that day silently and it was never paid; a period switched and
 * switched back would have taken the rest with it. Both were reasonable where they stood, and
 * neither can be written here — the only shrinking change has to hand over a list of keys.
 *
 * The one subtraction that is not a discard is a cell the owner has emptied himself — that is his
 * own keystroke on that cell, and an empty cell holds no work to lose. It goes rather than
 * lingering as a day that counts on his column heading and sends nothing.
 */
export function sheetAfter(sheet: Sheet, change: SheetChange): Sheet {
  switch (change.how) {
    case 'typed': {
      if (cellHasNothing(change.cell)) {
        if (sheet[change.key] === undefined) return sheet;
        return without(sheet, new Set([change.key]));
      }
      return { ...sheet, [change.key]: change.cell };
    }
    case 'filled': {
      // Answered cells win over a suggestion, HERE and not only at the call site: quietly restating
      // a typed Tuesday as the rostered one is how a correction gets undone by a convenience.
      const next = { ...sheet };
      for (const [key, cell] of Object.entries(change.cells)) {
        if (next[key] === undefined) next[key] = cell;
      }
      return next;
    }
    case 'discarded':
    case 'saved':
      return change.keys.length === 0 ? sheet : without(sheet, new Set(change.keys));
  }
}

function without(sheet: Sheet, keys: Set<string>): Sheet {
  const next: Sheet = {};
  for (const [key, cell] of Object.entries(sheet)) {
    if (!keys.has(key)) next[key] = cell;
  }
  return next;
}

/**
 * A sheet read back out of the browser's own store, or an empty one.
 *
 * Typed days survive leaving this screen (see the grid on why that is the honest mechanism), which
 * means a sheet can arrive from outside this run of the code — a half-written draft, a hand-edited
 * one, one written by a version of this file that has since changed. So it is parsed rather than
 * trusted: anything that is not a cell is dropped, and a store holding nonsense costs an empty
 * sheet rather than a screen that will not render. Nothing here can throw.
 */
export function parseDraft(raw: string | null | undefined): Sheet {
  if (!raw) return {};
  let held: unknown;
  try {
    held = JSON.parse(raw);
  } catch {
    return {};
  }
  if (held === null || typeof held !== 'object' || Array.isArray(held)) return {};
  const sheet: Sheet = {};
  for (const [key, value] of Object.entries(held as Record<string, unknown>)) {
    // A key that is not a person and a day is not a cell of this sheet — and one of the names that
    // is not, `__proto__`, would be an assignment to the prototype rather than a cell at all.
    if (!/^[^|]+\|\d{4}-\d{2}-\d{2}$/.test(key)) continue;
    if (value === null || typeof value !== 'object') continue;
    const { start, end, breakMinutes, hours } = value as Record<string, unknown>;
    if (typeof start !== 'string' || typeof end !== 'string' || typeof hours !== 'string') continue;
    const cell: Cell = {
      start,
      end,
      hours,
      breakMinutes: typeof breakMinutes === 'number' && Number.isFinite(breakMinutes) ? breakMinutes : 0,
    };
    if (!cellHasNothing(cell)) sheet[key] = cell;
  }
  return sheet;
}

/**
 * Every calendar day from one date to another, both ends counted.
 *
 * Local noon, never midnight: a date-only string parsed as UTC midnight is the previous day
 * everywhere west of Greenwich, which is to say everywhere in Honduras. The days come back out
 * through `toIsoDate`, which reads local calendar parts, so nothing here can shift a day by a
 * timezone. Capped at a year against a period typed with its dates the wrong way round or a year
 * apart — this list becomes rows on a screen, and a bad pair of dates should show as an empty
 * period rather than as a browser that stops responding.
 */
export function eachDay(from: string, to: string): string[] {
  const cursor = new Date(`${from}T12:00:00`);
  const end = new Date(`${to}T12:00:00`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime())) return [];
  const days: string[] = [];
  while (cursor.getTime() <= end.getTime() && days.length < 366) {
    const day = toIsoDate(cursor);
    if (!day) break;
    days.push(day);
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

/**
 * "930" → "09:30", "9" → "09:00", "16:00" unchanged. Null when the text is not a time of day.
 *
 * The forgiving half of "type a time and move on". Somebody entering a fortnight of shifts types
 * four digits and moves to the next box; making him reach for the colon two hundred times is most
 * of what makes the job feel long. Deliberately no am/pm: this bar's schedules, punches and
 * timesheets are all on the 24-hour clock, and a screen that accepted "9pm" here would be the only
 * one that read a shift differently from the rest of the system.
 */
export function readClock(text: string): string | null {
  const match = /^(\d{1,2})[:.]?(\d{2})?$/.exec(text.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = match[2] === undefined ? 0 : Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * The same, for putting back into the box a person has just left — nonsense is handed back
 * untouched rather than swallowed, so a typo stays visible under the cell that will refuse it.
 */
export function normalizeClock(text: string): string {
  return text.trim() === '' ? '' : (readClock(text) ?? text);
}

/**
 * What a day already on file is worth, in paid minutes — or null when only a rebuild can say.
 *
 * A PTO day with no minutes means "whatever the schedule expected of it", which is resolved when
 * the timesheets are built and is deliberately not guessed at here.
 */
export function recordedMinutes(entry: TimesheetDayEntry): number | null {
  if (entry.kind === 'pto') return entry.minutes;
  if (entry.totalsOnly) return entry.minutes;
  if (!entry.startTime || !entry.endTime) return null;
  const span = shiftSpanMinutes(entry.startTime, entry.endTime);
  return span === null ? null : Math.max(0, span - entry.unpaidBreakMinutes);
}

/**
 * A day already on file, as the boxes should show it. Null for a day this sheet does not write —
 * a paid day off, which has its own door and is left alone here.
 *
 * A CELL ONLY EVER HOLDS WHAT ITS OWN BOXES ASKED FOR, and both halves of that matter.
 *
 * A day recorded as a total prefills no times, in either mode. Its record says that nobody knows
 * when the hours ran; inventing a clock-in from the total would put words in its mouth, and the
 * invented shift would be indistinguishable from a real one the moment it was saved.
 *
 * And a TIMED day prefills nothing into an hours box. It is the more surprising half: carrying the
 * times along invisibly beside a box asking for hours means the cell is holding a shift the screen
 * is not showing, and the moment somebody types hours into it the sheet reads it as timed again and
 * throws the hours away. The times are said beside the box instead, where they can be read and not
 * silently acted on.
 */
export function cellOfRecord(entry: TimesheetDayEntry, mode: CellMode): Cell | null {
  if (entry.kind !== 'worked') return null;
  if (mode === 'hours') {
    return entry.totalsOnly && entry.minutes != null
      ? { ...BLANK_CELL, hours: minutesToText(entry.minutes) }
      : BLANK_CELL;
  }
  if (entry.totalsOnly) return BLANK_CELL;
  return {
    start: entry.startTime ?? '',
    end: entry.endTime ?? '',
    breakMinutes: entry.unpaidBreakMinutes,
    hours: '',
  };
}

/**
 * WHETHER THIS SHEET MAY WRITE THIS DAY AT ALL — asked on the SEND path, not only in the render.
 *
 * A paid day off is shown here and left alone: it has a door of its own where its reason and its
 * note belong with it. The render already knew that; the loop that gathered what to send did not,
 * and walked the typed cells without ever consulting the record beside them. What that costs is
 * not a rejected entry — the bulk door reuses the row's id, so a worked day sent over a day off
 * REPLACES it, and the day off, its reason and its note are gone with nothing said.
 *
 * A day with no record at all is writable, which is the ordinary case. `undefined` therefore has to
 * mean "there is no record", never "the records have not arrived yet" — the caller owes that
 * distinction, because a sheet that cannot yet see a day off must not write over it either.
 */
export function sheetMayWrite(record: TimesheetDayEntry | undefined): boolean {
  return record === undefined || record.kind === 'worked';
}

/** A scheduled day, as the shift it suggests. */
export function cellOfSchedule(day: PatternDay): Cell {
  return { start: day.startTime, end: day.endTime, breakMinutes: day.unpaidBreakMinutes, hours: '' };
}

/** Nothing typed here at all — which is "no record", never "zero hours worked". */
export function cellIsBlank(cell: Cell, mode: CellMode): boolean {
  return mode === 'hours' ? cell.hours.trim() === '' : cell.start.trim() === '' && cell.end.trim() === '';
}

/**
 * WHICH WAY A CELL IS BEING WRITTEN — read off WHAT IS IN IT, in both directions.
 *
 * The switch above the grid chooses what an EMPTY cell offers and nothing more. Whatever somebody
 * has actually typed decides for itself, because a cell read in the other mode reads as empty, and
 * an empty cell is not sent, not counted, not totalled and not warned about: a day of work vanishes
 * from a screen that still looks full, and the next save takes the trace with it.
 *
 * It has to be symmetric or it is not a rule. Guarding one direction only — times survive the
 * switch going on, hours do not survive it going off — is where twelve days typed as hours went
 * when the owner turned the switch off to type the one shift he had times for.
 *
 * TIMES STILL WIN over hours when a cell somehow holds both, for the reason every sentence in this
 * feature gives: they are the better record, and a day that has them keeps them.
 */
export function modeOfCell(cell: Cell | undefined, offered: CellMode): CellMode {
  if (cell === undefined) return offered;
  if (cell.start.trim() !== '' || cell.end.trim() !== '') return 'times';
  if (cell.hours.trim() !== '') return 'hours';
  return offered;
}

/**
 * Paid minutes a cell describes, or null while it does not describe a day yet.
 *
 * The midnight crossing is `shiftSpanMinutes`' answer and not a second one — a bar's week is full
 * of shifts that end at 02:00, and two readings of that is the one arithmetic error on this screen
 * nobody would see until it had been paid.
 */
export function cellMinutes(cell: Cell, mode: CellMode): number | null {
  if (mode === 'hours') return parseHoursInput(cell.hours);
  const span = shiftSpanMinutes(cell.start.trim(), cell.end.trim());
  if (span === null) return null;
  const paid = span - cell.breakMinutes;
  return paid > 0 ? paid : null;
}

/**
 * Why this cell cannot be saved, in the words that fit under it — or null when it is fine or empty.
 *
 * The same refusals the server would give, met while the box still has focus. That is not
 * belt-and-braces: the save is ALL OR NOTHING, so one mistyped cell in a month refuses the other
 * hundred and eighty, and catching it here is the difference between a typo and a wasted afternoon.
 *
 * `hoursAllowed` is whether THIS DAY may be written as hours at all — the owner's date, which
 * moves. A cell holding hours on a day that has since come onto the clock is refused out loud here
 * rather than quietly re-read as an empty times cell: the hours stay in their box, in front of the
 * person who typed them, under the one sentence that says what to do about them.
 */
export function cellFault(cell: Cell, mode: CellMode, hoursAllowed: boolean): string | null {
  if (cellIsBlank(cell, mode)) return null;
  if (mode === 'hours') {
    if (!hoursAllowed) return 'This day is on the clock — clear the hours and put its times in';
    return parseHoursInput(cell.hours) === null ? 'Hours read 8, 7.5 or 7:30' : null;
  }
  const start = readClock(cell.start);
  const end = readClock(cell.end);
  if (cell.start.trim() !== '' && start === null) return 'Times read 16:00';
  if (cell.end.trim() !== '' && end === null) return 'Times read 16:00';
  if (start === null || end === null) return 'Both times, or neither';
  if (start === end) return 'A shift cannot start and end at the same moment';
  const span = shiftSpanMinutes(start, end);
  if (span !== null && cell.breakMinutes >= span) return 'The unpaid break is longer than this shift';
  return null;
}

/**
 * ONE READING OF ONE CELL, which everything on the screen then agrees about.
 *
 * The boxes shown, the minutes in the column heading, the count on the button, the "will not be
 * saved" warning and what actually goes on the wire all have to be answers about the same cell in
 * the same mode. When they were worked out separately they disagreed, and every one of those
 * disagreements was a day that showed on the screen and was never paid.
 */
export interface CellReading {
  /** The mode the cell's own contents put it in. */
  mode: CellMode;
  /** Nothing in the boxes that mode shows — "not got to it yet", never zero hours. */
  blank: boolean;
  /** What it is worth, or null while it is empty or refused. Never a figure that cannot be saved. */
  minutes: number | null;
  fault: string | null;
}

/**
 * `offered` is what an empty cell should offer — the switch and the date. `hoursAllowed` is whether
 * this day may be written as hours at all, which is the owner's date on its own.
 */
export function readCell(cell: Cell | undefined, offered: CellMode, hoursAllowed: boolean): CellReading {
  const held = cell ?? BLANK_CELL;
  const mode = modeOfCell(cell, offered);
  const blank = cellIsBlank(held, mode);
  const fault = cellFault(held, mode, hoursAllowed);
  return { mode, blank, fault, minutes: blank || fault !== null ? null : cellMinutes(held, mode) };
}

/**
 * One day of the sheet, in the shape the bulk endpoint takes.
 *
 * NO `note`, and the absence is load-bearing rather than an omission. The bulk door leaves a day's
 * note alone unless the field arrives, precisely because this grid has no note box — so a row that
 * sent `note: undefined` explicitly would be indistinguishable from a screen with something to say,
 * and correcting one clock-out here would delete the sentence explaining why the day was entered by
 * hand. The grid shows the note instead, so nobody edits a day blind to it.
 */
export interface DayEntryPut {
  staffId: string;
  date: string;
  kind: 'worked';
  startTime?: string;
  endTime?: string;
  unpaidBreakMinutes?: number;
  minutes?: number;
  totalsOnly?: boolean;
}

/**
 * A cell as one entry on the wire, or null when it is empty or does not read as a day.
 *
 * The reading is passed in rather than taken again, so that what the screen shows under a cell and
 * what this puts on the wire cannot be two answers about the same box.
 *
 * The times are normalized on the way out so that "930" typed and never blurred still leaves as
 * "09:30" — a save must not depend on which box the owner happened to be in when he pressed it.
 */
export function entryOf(
  staffId: string,
  date: string,
  cell: Cell,
  { mode, blank, fault }: CellReading,
): DayEntryPut | null {
  if (blank || fault !== null) return null;
  if (mode === 'hours') {
    const minutes = parseHoursInput(cell.hours);
    return minutes === null ? null : { staffId, date, kind: 'worked', totalsOnly: true, minutes };
  }
  const startTime = readClock(cell.start);
  const endTime = readClock(cell.end);
  if (startTime === null || endTime === null) return null;
  return { staffId, date, kind: 'worked', startTime, endTime, unpaidBreakMinutes: cell.breakMinutes };
}

/**
 * WHICH CELL A WHOLESALE REFUSAL WAS ABOUT.
 *
 * A month is sent all or nothing, so a refused save writes nothing at all — an answer that is
 * unactionable on a grid of two hundred cells unless it also says which one to look at. The server
 * names the entry by its position in the array it was sent, with the person and the day beside it;
 * this reads that off the refusal and hands back nothing at all if the shape is not there, because
 * a screen that threw away a typed month over a missing field would be the worse failure by far.
 */
export function refusedEntry(err: unknown): { index: number; staffId: string; date: string } | null {
  if (!(err instanceof ApiError)) return null;
  const named = (err.body as { entry?: unknown } | null | undefined)?.entry;
  if (named === null || typeof named !== 'object') return null;
  const { index, staffId, date } = named as { index?: unknown; staffId?: unknown; date?: unknown };
  if (typeof index !== 'number' || typeof staffId !== 'string' || typeof date !== 'string') return null;
  return { index, staffId, date };
}
